import React, { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Target, 
  Users, 
  Heart, 
  Lightbulb, 
  CheckCircle, 
  Link,
  Puzzle,
  Shield,
  Pencil,
  Check,
  X,
  Loader2
} from "lucide-react";
import BriefNotesList from "./BriefNotesList";
import type { Challenge } from "./ChallengeBoard";

interface ChallengeBriefSectionsProps {
  challenge: Challenge;
  isTeamMember?: boolean;
  isAdmin?: boolean;
  canEdit?: boolean;
  editModeActive?: boolean;
  onUpdate?: () => void;
}

type BriefField = 
  | "problem_description"
  | "who_it_affects"
  | "why_it_matters"
  | "opportunity"
  | "solution_directions"
  | "inputs_dependencies"
  | "risks_guardrails"
  | "success_metrics"
  | "references_contacts";

const ChallengeBriefSections: React.FC<ChallengeBriefSectionsProps> = ({
  challenge,
  isTeamMember = false,
  isAdmin = false,
  canEdit = false,
  editModeActive = false,
  onUpdate,
}) => {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);

  const sections = [
    {
      id: "problem",
      title: "What's Broken",
      icon: AlertTriangle,
      content: challenge.problem_description,
      field: "problem_description" as BriefField,
    },
    {
      id: "who",
      title: "Who It Affects",
      icon: Users,
      content: challenge.who_it_affects,
      field: "who_it_affects" as BriefField,
    },
    {
      id: "why",
      title: "Why It Matters",
      icon: Heart,
      content: challenge.why_it_matters,
      field: "why_it_matters" as BriefField,
    },
    {
      id: "opportunity",
      title: "What Could Be Built",
      icon: Lightbulb,
      content: challenge.opportunity,
      field: "opportunity" as BriefField,
    },
    {
      id: "solutions",
      title: "Solution Directions",
      icon: Puzzle,
      content: challenge.solution_directions,
      field: "solution_directions" as BriefField,
      isList: true,
    },
    {
      id: "inputs",
      title: "Inputs & Dependencies",
      icon: Link,
      content: challenge.inputs_dependencies,
      field: "inputs_dependencies" as BriefField,
    },
    {
      id: "risks",
      title: "Risks & Guardrails",
      icon: Shield,
      content: challenge.risks_guardrails,
      field: "risks_guardrails" as BriefField,
    },
    {
      id: "success",
      title: "What Success Looks Like",
      icon: Target,
      content: challenge.success_metrics,
      field: "success_metrics" as BriefField,
    },
    {
      id: "references",
      title: "References & Contacts",
      icon: CheckCircle,
      content: challenge.references_contacts,
      field: "references_contacts" as BriefField,
    },
  ].filter((s) => s.content && (Array.isArray(s.content) ? s.content.length > 0 : true) || canEdit);

  const handleSave = async (field: BriefField, isList: boolean) => {
    setSaving(true);
    try {
      const value = isList
        ? editedContent.split("\n").map((l) => l.trim()).filter(Boolean)
        : editedContent.trim() || null;

      const { error } = await supabase
        .from("challenges")
        .update({ [field]: value })
        .eq("id", challenge.id);

      if (error) throw error;
      toast.success("Section updated");
      setEditingSection(null);
      onUpdate?.();
    } catch (error) {
      console.error("Error updating section:", error);
      toast.error("Failed to update section");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (sectionId: string, content: string | string[] | null, isList: boolean) => {
    setEditingSection(sectionId);
    if (isList && Array.isArray(content)) {
      setEditedContent(content.join("\n"));
    } else {
      setEditedContent((content as string) || "");
    }
  };

  return (
    <Accordion type="multiple" defaultValue={["problem", "opportunity"]} className="space-y-2">
      {sections.map((section) => (
        <AccordionItem key={section.id} value={section.id} className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-3">
              <section.icon className="h-5 w-5 text-primary" />
              <span className="font-medium">{section.title}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {editingSection === section.id ? (
              <div className="space-y-2">
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={section.isList ? 6 : 4}
                  placeholder={section.isList ? "One item per line..." : "Enter content..."}
                  autoFocus
                />
                {section.isList && (
                  <p className="text-xs text-muted-foreground">Enter one item per line</p>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleSave(section.field, !!section.isList)}
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingSection(null)}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="group relative">
                {section.isList && Array.isArray(section.content) ? (
                  <ul className="space-y-2">
                    {section.content.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : section.content ? (
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {section.content as string}
                  </p>
                ) : (
                  <p className="text-muted-foreground/50 italic">No content yet</p>
                )}
                {canEdit && editModeActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => startEditing(section.id, section.content, !!section.isList)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
            
            {/* Team Insights/Notes Section */}
            <div className="pt-4 border-t border-border/50">
              <BriefNotesList
                challengeId={challenge.id}
                sectionId={section.id}
                isTeamMember={isTeamMember}
                isAdmin={isAdmin}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default ChallengeBriefSections;
